import apiClient from './apiClient';

export type IngestionSourceType = 'mixed' | 'builder' | 'legacy_program' | 'legacy_registrants' | 'manual';
export type IngestionRunState = 'uploaded' | 'parsing' | 'mapping_required' | 'matching' | 'review_required' | 'ready' | 'partially_published' | 'published' | 'failed' | 'archived';
export type IngestionEntityType = 'session' | 'speaker' | 'paper' | 'attendee' | 'exhibitor';
export type MappableEntityType = 'session' | 'speaker';
export type DraftOperation = 'create' | 'update' | 'delete';
export type DraftStatus = 'review_required' | 'ready' | 'excluded' | 'published' | 'confirmed';
export type ContentArea = 'schedule' | 'speakers' | 'papers' | 'attendees' | 'exhibitors';
export type PublishableArea = 'schedule' | 'speakers';
export type OrganizerRole = 'admin' | 'manager';

export interface IngestionLimits {
  accepted_extensions: string[];
  max_files: number;
  max_file_bytes: number;
}

export interface SourceTable {
  table_key: string;
  filename: string;
  sheet_name: string | null;
  headers: string[];
  sample_rows: Array<Record<string, unknown>>;
  row_count: number;
}

export interface FieldMapping {
  entity_type: MappableEntityType;
  columns: Record<string, string>;
  source_system?: string;
  unresolved_required_fields?: string[];
  ambiguous_fields?: Record<string, string[]>;
}

export interface IngestionMapping {
  id: string;
  file_id: string;
  table_key: string;
  revision: number;
  entity_type: MappableEntityType;
  mapping: FieldMapping;
  confirmed: boolean;
  confirmed_at: string | null;
}

export interface SourceFile {
  id: string;
  run_id: string;
  filename: string;
  media_type: string | null;
  byte_size: number;
  source_sha256: string;
  detected_kind: string;
  state: string;
  error: Record<string, unknown> | null;
  created_at: string;
  tables: SourceTable[];
  mappings: IngestionMapping[];
}

export interface IngestionRun {
  id: string;
  event_id: string;
  state: IngestionRunState;
  source_type: IngestionSourceType;
  summary: Record<string, unknown>;
  failure: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  limits?: IngestionLimits | null;
  files: SourceFile[];
}

export interface AreaCounts { published_count: number; draft_count: number }
export interface AreaReadiness { state?: string; errors?: number; warnings?: number; issues?: unknown[]; [key: string]: unknown }

export interface IngestionDashboard {
  enabled: boolean;
  event_override: boolean | null;
  effective_role: OrganizerRole;
  limits: IngestionLimits;
  readiness: Partial<Record<ContentArea, AreaReadiness>>;
  area_counts: Record<ContentArea, AreaCounts>;
  newest_resumable_run: IngestionRun | null;
}

export interface DraftChange {
  id: string;
  event_id: string;
  run_id: string | null;
  source_record_id: string | null;
  entity_type: IngestionEntityType;
  operation: DraftOperation;
  classification: string;
  target_id: string | null;
  approved_data: Record<string, unknown>;
  proposed_data: Record<string, unknown>;
  organizer_overrides: string[];
  status: DraftStatus;
  validation: Record<string, unknown>;
  match_proposal: Record<string, unknown> | null;
  published_data: Record<string, unknown> | null;
  source_locator: Record<string, unknown> | null;
  issues: Array<{ id: string; code: string; severity: 'error' | 'warning' | 'info'; field: string | null; message: string }>;
  created_at: string;
  updated_at: string;
}

export interface PublicationSelection { areas?: PublishableArea[]; all_ready?: boolean }
export interface PublicationPreview { change_ids: string[]; content_areas: string[]; summary: Record<string, number> }
export interface Publication {
  id: string; event_id: string; content_areas: string[]; state: 'preparing' | 'completed' | 'failed';
  summary: Record<string, unknown>; published_by: string | null; created_at: string; completed_at: string | null;
}
export interface PublicationItem {
  id: string; draft_change_id: string | null; entity_type: string; operation: DraftOperation; entity_id: string;
  before_data: Record<string, unknown> | null; after_data: Record<string, unknown> | null;
  relationship_changes: Array<Record<string, unknown>>; source_provenance: Record<string, unknown>; created_at: string;
}
export interface PublicationDetail extends Publication { items: PublicationItem[] }
export interface DraftRelationship {
  id: string; event_id: string; relationship_type: 'session_speaker';
  from_change_id: string; from_published_id: string | null;
  to_change_id: string | null; to_published_id: string | null;
  status: 'proposed' | 'approved' | 'rejected' | 'published'; source: string; created_at: string;
}

const base = (eventId: string, path = '') => `/admin/events/${encodeURIComponent(eventId)}/content-ingestion${path}`;
const part = (value: string) => encodeURIComponent(value);

export const contentIngestionService = {
  dashboard: (eventId: string) => apiClient.get<IngestionDashboard>(base(eventId, '/dashboard')),
  createRun: (eventId: string, sourceType: IngestionSourceType = 'mixed') =>
    apiClient.post<IngestionRun>(base(eventId, '/runs'), { source_type: sourceType }),
  runs: (eventId: string) => apiClient.get<IngestionRun[]>(base(eventId, '/runs')),
  run: (eventId: string, runId: string) => apiClient.get<IngestionRun>(base(eventId, `/runs/${part(runId)}`)),
  archiveRun: (eventId: string, runId: string) => apiClient.delete<void>(base(eventId, `/runs/${part(runId)}`)),
  uploadFiles: (eventId: string, runId: string, files: File[]) => {
    const body = new FormData();
    files.forEach((file) => body.append('files', file));
    return apiClient.upload<SourceFile[]>(base(eventId, `/runs/${part(runId)}/files`), body);
  },
  removeFile: (eventId: string, runId: string, fileId: string) =>
    apiClient.delete<void>(base(eventId, `/runs/${part(runId)}/files/${part(fileId)}`)),
  confirmMapping: (eventId: string, runId: string, fileId: string, input: { table_key: string; mapping: FieldMapping }) =>
    apiClient.put<IngestionMapping>(base(eventId, `/runs/${part(runId)}/mappings/${part(fileId)}`), input),
  process: (eventId: string, runId: string) => apiClient.post<IngestionRun>(base(eventId, `/runs/${part(runId)}/process`), {}),
  drafts: (eventId: string, filters: { area?: ContentArea; status?: DraftStatus; q?: string } = {}) => {
    const query = new URLSearchParams();
    if (filters.area) query.set('area', filters.area);
    if (filters.status) query.set('status', filters.status);
    if (filters.q) query.set('q', filters.q);
    return apiClient.get<DraftChange[]>(base(eventId, `/drafts${query.size ? `?${query}` : ''}`));
  },
  createDraft: (eventId: string, entityType: IngestionEntityType, data: Record<string, unknown>) =>
    apiClient.post<DraftChange>(base(eventId, '/drafts'), { entity_type: entityType, data }),
  patchDraft: (eventId: string, changeId: string, input: { patch?: Record<string, unknown>; match_target_id?: string; match_target_change_id?: string; missing_decision?: 'keep' | 'delete' }) =>
    apiClient.patch<DraftChange>(base(eventId, `/drafts/${part(changeId)}`), input),
  bulkPatch: (eventId: string, changeIds: string[], patch: Record<string, unknown>) =>
    apiClient.post<DraftChange[]>(base(eventId, '/drafts/bulk'), { change_ids: changeIds, patch }),
  removeDraft: (eventId: string, changeId: string) => apiClient.delete<void>(base(eventId, `/drafts/${part(changeId)}`)),
  exclude: (eventId: string, changeId: string) => apiClient.post<DraftChange>(base(eventId, `/drafts/${part(changeId)}/exclude`), {}),
  restore: (eventId: string, changeId: string) => apiClient.post<DraftChange>(base(eventId, `/drafts/${part(changeId)}/restore`), {}),
  stagePublishedUpdate: (eventId: string, entityType: IngestionEntityType, targetId: string, patch: Record<string, unknown>) =>
    apiClient.patch<DraftChange>(base(eventId, `/drafts/published/${part(entityType)}/${part(targetId)}`), { patch }),
  stagePublishedDelete: (eventId: string, entityType: IngestionEntityType, targetId: string) =>
    apiClient.delete<DraftChange>(base(eventId, `/drafts/published/${part(entityType)}/${part(targetId)}`)),
  createRelationship: (eventId: string, fromChangeId: string, destination: { to_change_id?: string; to_published_id?: string }) =>
    apiClient.post<DraftRelationship>(base(eventId, '/drafts/relationships'), {
      relationship_type: 'session_speaker', from_change_id: fromChangeId, ...destination,
    }),
  downloadErrors: (eventId: string, runId: string) => apiClient.downloadBlob(base(eventId, `/runs/${part(runId)}/errors.csv`), `content-ingestion-errors-${runId}.csv`),
  downloadTemplate: (eventId: string) => apiClient.downloadBlob(base(eventId, '/template.xlsx'), 'event-content-template.xlsx'),
  updateSettings: (eventId: string, enabled: boolean) => apiClient.patch<{ enabled: boolean; event_override: boolean | null }>(base(eventId, '/settings'), { enabled }),
  previewPublication: (eventId: string, selection: PublicationSelection) => apiClient.post<PublicationPreview>(base(eventId, '/publications/preview'), selection),
  publish: (eventId: string, selection: PublicationSelection) => apiClient.post<Publication[]>(base(eventId, '/publications'), selection),
  publications: (eventId: string) => apiClient.get<Publication[]>(base(eventId, '/publications')),
  publication: (eventId: string, publicationId: string) => apiClient.get<PublicationDetail>(base(eventId, `/publications/${part(publicationId)}`)),
  createRollbackDraft: (eventId: string, publicationId: string) => apiClient.post<IngestionRun>(base(eventId, `/publications/${part(publicationId)}/rollback-draft`), {}),
};

export default contentIngestionService;
